import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Utils
import { dateMMMDDYYYY, dateMMDDYY } from 'Shared/utils/DateTime.utils';
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import Avatar from 'Shared/components/Avatar/Avatar';
import GoalIcon from 'Shared/components/Goals/GoalIcon';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { GoalItem } from 'Shared/constants/types';
import GoalStatusChangeDropdown from './GoalStatusChangeDropdown';
import { WpRichTextInput } from 'Wpds/components/rich-text';

// Styles
import './GoalInformation.scss';

type Props = {
  goal: GoalItem;
  handleMarkComplete?: Function;
  handleMarkIncomplete?: Function;
  toggleEdit?: Function;
  toggleClone?: Function;
  actionItemsLength?: number;
  isSavingComplete?: boolean;
};

const GoalInformation = ({
  goal,
  handleMarkComplete,
  handleMarkIncomplete,
  toggleEdit,
  toggleClone,
  actionItemsLength,
  isSavingComplete,
}: Props) => {
  const assigneeId = goal.assigneeId;
  const assigneeUserId = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: assigneeId })).toJS();
  const { id: currentUserId } = useCurrentUser();
  const goalResolutionNote = goal.resolutionNote;

  const description = goal.description;
  const showDescription = !!description && !isEmpty(description);
  const userName = assigneeUserId.label;
  const endDate = dateMMMDDYYYY(goal.endDate);
  const nextCheckin = goal.nextCheckin ? dateMMMDDYYYY(goal.nextCheckin) : null;
  const completedAt = dateMMDDYY(goal.completedAt);

  const completerName = useSelector(
    state => UsersSelectors.getUserByUserId(state, { userId: goal?.completedById })?.toJS()?.displayName
  );
  const completerText = completerName ? `by ${completerName}` : null;

  const status = goal.status;
  const isComplete = goal.complete;

  const canChangeStatus = currentUserId === goal.assignerId || currentUserId === goal.assigneeId;
  // if user cannot edit goal, and goal has no status (status === no_status), let's not show status
  const showStatus = canChangeStatus || (!canChangeStatus && status !== 'no_status');

  const classes = classNames('GoalInformation', `GoalInformation--status-${status}`);

  const handleEditClick = useCallback(() => {
    toggleEdit();
  }, [toggleEdit]);

  const handleCloneClick = useCallback(() => {
    toggleClone();
  }, [toggleClone]);

  const handleMarkCompleteClick = useCallback(() => {
    handleMarkComplete();
  }, [handleMarkComplete]);

  const handleMarkIncompleteClick = useCallback(() => {
    handleMarkIncomplete();
  }, [handleMarkIncomplete]);

  return (
    <div className={classes}>
      <div className="GoalInformation__header">
        <GoalIcon icon={goal.icon} status={status} />
        <h4 className="GoalInformation__header__title">{goal.name}</h4>
      </div>
      {isComplete && (
        <div className="GoalInformation__banner">
          <WpAlert type="success" iconPlacement="top">
            <div className="GoalInformation__banner__content">
              <div className="GoalInformation__banner__content__title">
                This goal was marked complete on {completedAt} {completerText}
              </div>
              {goalResolutionNote && (
                <>
                  <div className="GoalInformation__banner__content__note">Resolution notes</div>
                  <div>{goalResolutionNote}</div>
                </>
              )}
            </div>
          </WpAlert>
        </div>
      )}
      {canChangeStatus && (
        <div className="GoalInformation__actions">
          <WpButtonGroup>
            {isComplete ? (
              <WpButton onClick={handleMarkIncompleteClick} variant="secondary" loading={isSavingComplete}>
                Mark as Incomplete
              </WpButton>
            ) : (
              <WpButton
                onClick={handleMarkCompleteClick}
                variant={actionItemsLength ? 'primary' : 'secondary'}
                loading={isSavingComplete}
              >
                Mark as Complete
              </WpButton>
            )}
            {!isComplete && (
              <WpButton variant="secondary" onClick={handleEditClick}>
                Edit
              </WpButton>
            )}
            {!isComplete && (
              <WpDropdown placement="bottom-end">
                <WpDropdownToggle>
                  <WpButton variant="tertiary" qaTest="goals-actions-dropdown">
                    <WpIcon name="ellipsis-h-alt" />
                  </WpButton>
                </WpDropdownToggle>
                <WpDropdownMenu>
                  <WpDropdownMenuItem onClick={handleCloneClick}>Copy Goal</WpDropdownMenuItem>
                </WpDropdownMenu>
              </WpDropdown>
            )}
          </WpButtonGroup>
        </div>
      )}
      <div className="GoalInformation__details">
        <div className="GoalInformation__details__item">
          <span className="GoalInformation__details__item__label">Owner</span>
          <div className="GoalInformation__details__item__value">
            <Avatar avatar={assigneeUserId.avatar} height={32} />
            <p className="ml-2 mb-0">{userName}</p>
          </div>
        </div>
        <div className="GoalInformation__details__item">
          <span className="GoalInformation__details__item__label">End Date</span>
          <div className="GoalInformation__details__item__value">
            <p className="mb-0">{endDate}</p>
          </div>
        </div>
        {nextCheckin && (
          <div className="GoalInformation__details__item">
            <span className="GoalInformation__details__item__label">Next Check-in</span>
            <div className="GoalInformation__details__item__value">
              <p className="mb-0">{nextCheckin}</p>
            </div>
          </div>
        )}
        {showStatus && (
          <div className="GoalInformation__details__item GoalInformation__details__item--status">
            <span className="GoalInformation__details__item__label">Status</span>
            <div className="GoalInformation__details__item__value">
              <GoalStatusChangeDropdown goal={goal} />
            </div>
          </div>
        )}
      </div>
      {showDescription && (
        <div className="GoalInformation__description">
          <span className="GoalInformation__description__label">Description</span>
          <WpRichTextInput
            value={description}
            readOnly
            options={{ showBorder: false, backgroundColor: ColorConstants.gray[50] }}
            height="auto"
          />
        </div>
      )}
    </div>
  );
};

export default GoalInformation;
